import type { GroupProps } from '@mantine/core';
import { Group, Chip as MantineChip } from '@mantine/core';
import { useId, type ReactNode } from 'react';
import styled, { css } from 'styled-components';

interface ChipProps {
  value?: string;
  checked?: boolean;
  onChange?: (checked: boolean) => void;
  children: ReactNode;
  className?: string;
  onClick?: (event: React.MouseEvent<HTMLInputElement>) => void;
  disabled?: boolean;
}

export const Chip = ({
  value,
  checked,
  onChange,
  children,
  className,
  onClick,
  disabled,
}: ChipProps) => {
  const id = useId();
  return (
    <StyledMantineChip
      value={value}
      className={className}
      checked={checked}
      onChange={onChange}
      id={id}
      size="md"
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </StyledMantineChip>
  );
};

export interface ChipGroupProps<
  T extends string = string,
  M extends boolean = false,
> extends Omit<GroupProps, 'children' | 'value' | 'onChange'> {
  children: ReactNode;
  value?: M extends true ? T[] : T | null;
  onChange?: (value: M extends true ? T[] : T) => void;
  multiple?: M;
}

function ChipGroup<T extends string = string, M extends boolean = false>({
  children,
  value,
  onChange,
  multiple = false as M,
  ...rest
}: ChipGroupProps<T, M>) {
  return (
    <MantineChip.Group<M>
      value={value}
      onChange={onChange as (value: string | string[]) => void}
      multiple={multiple}
    >
      <Group {...rest}>{children}</Group>
    </MantineChip.Group>
  );
}

Chip.Group = ChipGroup;

export default Chip;

const StyledMantineChip = styled(MantineChip)`
  ${({ theme }) => css`
    .mantine-Chip-label {
      ${theme.typography.bodyMd1};
      padding: ${theme.spacing(1, 2)};

      &:not([data-disabled='true']) {
        background-color: ${theme.palette.neutral['10']};
        color: ${theme.palette.neutral[100]};
        border-color: ${theme.palette.neutral[60]};

        &:hover {
          background-color: ${theme.palette.neutral[50]};
          color: ${theme.palette.neutral[100]};
          border-color: ${theme.palette.neutral[60]};
        }

        &[data-checked='true'] {
          background-color: ${theme.palette.neutral[90]};
          color: ${theme.palette.neutral['00']};
          border-color: transparent;
        }
      }
    }

    .mantine-Chip-iconWrapper {
      display: none;
    }
  `}
`;
